'use client'

import { useEffect, useState } from 'react'

import { type PortableTextBlock } from 'next-sanity'
import { useIsPresentationTool } from 'next-sanity/hooks'

import { Icon } from '@/app/components/Icon'
import CustomPortableText from '@/app/components/PortableText'
import PortableTextMinimal from '@/app/components/PortableTextMinimal'
import { TRACKING_LOCATIONS, TrackingLocation } from '@/app/utils/analytics'

type AccordionProps = {
  value: {
    heading: PortableTextBlock[]
    content: PortableTextBlock[]
  }
  tracking: TrackingLocation
  documentId?: string
  documentType?: string
  parentPath?: string
}

export function Accordion({
  value,
  tracking,
  documentId,
  documentType,
  parentPath,
}: AccordionProps) {
  const isPresentationTool = useIsPresentationTool()
  const [isOpen, setIsOpen] = useState(false)

  // Keep accordion open in Sanity presentation mode
  useEffect(() => {
    if (isPresentationTool) {
      setIsOpen(true)
    }
  }, [isPresentationTool])

  if (!value.heading || !value.content) {
    return null
  }

  return (
    <div className="overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-5xl transition-colors text-left cursor-pointer"
        aria-expanded={isOpen}
      >
        <p className="flex-1 size-subheading-md lg:size-subheading-xl text-primary-700 font-bold">
          <PortableTextMinimal
            value={value.heading as PortableTextBlock[]}
            tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
          />
        </p>
        <div
          className="transition-transform duration-200 flex-shrink-0"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <Icon
            name="faChevronDown"
            provider="fontawesome"
            style="solid"
            size={20}
            className="text-primary-700"
          />
        </div>
      </button>

      {isOpen && (
        <div className="flex flex-col mb-5xl">
          <CustomPortableText
            value={value.content}
            tracking={tracking}
            documentId={documentId}
            documentType={documentType}
            parentPath={parentPath}
          />
        </div>
      )}

      <div className="h-[2px] bg-gradient-to-l from-purple-100 via-blue-100 to-green-100" />
    </div>
  )
}
